import { useSelector } from "react-redux";
import { mainSplitApi } from "@/src-refactor/modules/store/qrtk/mainSplitApi";
import { RootState } from "@/src-refactor/modules/store/store";

type QueryStatus = "uninitialized" | "pending" | "fulfilled" | "rejected";

interface PropertyListQueryState {
  status: QueryStatus;
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: unknown;
}

const DEFAULT_STATE: PropertyListQueryState = {
  status: "uninitialized",
  isUninitialized: true,
  isLoading: false,
  isFetching: false,
  isSuccess: false,
  isError: false
};

const STATUS_PRIORITY: Record<QueryStatus, number> = {
  uninitialized: 0,
  fulfilled: 1,
  pending: 2,
  rejected: 3
};

const normalizeStatus = (status?: string, isFetching?: boolean): QueryStatus => {
  if (status === "rejected") return "rejected";
  if (isFetching || status === "pending") return "pending";
  if (status === "fulfilled") return "fulfilled";
  return "uninitialized";
};

const getQueryTimestamp = (query: {
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
}) => Math.max(query.startedTimeStamp ?? 0, query.fulfilledTimeStamp ?? 0);

const selectPropertyListQueryState = (state: RootState): PropertyListQueryState => {
  const queries = state?.[mainSplitApi.reducerPath]?.queries;
  if (!queries) return DEFAULT_STATE;

  const propertyQueries = Object.values(queries).filter(entry => {
    if (!entry || typeof entry !== "object") return false;

    const query = entry as {
      endpointName?: string;
    };

    return query.endpointName === "getPropertyList";
  });

  if (propertyQueries.length === 0) {
    return DEFAULT_STATE;
  }

  const latestQuery = propertyQueries.reduce<{
    status?: string;
    isFetching?: boolean;
    error?: unknown;
    startedTimeStamp?: number;
    fulfilledTimeStamp?: number;
  } | null>((currentLatest, entry) => {
    const query = entry as {
      status?: string;
      isFetching?: boolean;
      error?: unknown;
      startedTimeStamp?: number;
      fulfilledTimeStamp?: number;
    };

    if (!currentLatest) {
      return query;
    }

    const currentTimestamp = getQueryTimestamp(currentLatest);
    const nextTimestamp = getQueryTimestamp(query);

    if (nextTimestamp > currentTimestamp) {
      return query;
    }

    if (nextTimestamp === currentTimestamp) {
      const currentStatus = normalizeStatus(
        currentLatest.status,
        currentLatest.isFetching
      );
      const nextStatus = normalizeStatus(query.status, query.isFetching);

      if (STATUS_PRIORITY[nextStatus] >= STATUS_PRIORITY[currentStatus]) {
        return query;
      }
    }

    return currentLatest;
  }, null);

  if (!latestQuery) {
    return DEFAULT_STATE;
  }

  const normalizedStatus = normalizeStatus(
    latestQuery.status,
    latestQuery.isFetching
  );

  return {
    status: normalizedStatus,
    isUninitialized: normalizedStatus === "uninitialized",
    isLoading: normalizedStatus === "pending",
    isFetching: Boolean(latestQuery.isFetching),
    isSuccess: normalizedStatus === "fulfilled",
    isError: normalizedStatus === "rejected",
    error: latestQuery.error
  };
};

const usePropertyListLoading = () => useSelector(selectPropertyListQueryState);

export { selectPropertyListQueryState };
export type { PropertyListQueryState };
export default usePropertyListLoading;
